import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Sparkles, X, Loader2, ClipboardCheck, ChevronDown, Save } from 'lucide-react';
import type { ContentBlockShape } from '@/lib/api';
import { api } from '@/lib/api';
import { parsePayload } from './types';
import { cn } from '@/lib/utils';

/**
 * Conversion engine UI for a lesson — Phase 6.
 *
 * Reads the lesson's text-bearing blocks (text + heading + document), joins
 * the prose into one string, sends it to POST /ai/convert with the chosen
 * target type, and shows the result in a preview modal so the instructor
 * can inspect the JSON before deciding what to do with it.
 *
 * The "Save as new module item" follow-up (which would create an Assessment
 * for `quiz`/`knowledge_check` and a new lesson for the others) is
 * intentionally deferred — this commit ships the AI hop + preview so the
 * end-to-end conversion is testable. Wiring the materialize step into the
 * existing assessment / module-item endpoints is mechanical and lands next.
 */
type Target = 'quiz' | 'flashcards' | 'slide_deck' | 'lesson_plan' | 'rubric';

const TARGETS: Array<{ key: Target; label: string; hint: string }> = [
  { key: 'quiz', label: 'Quiz', hint: 'Multiple-choice questions' },
  { key: 'flashcards', label: 'Flashcards', hint: 'Front/back study cards' },
  { key: 'slide_deck', label: 'Slide deck', hint: 'Headings + bullets per slide' },
  { key: 'lesson_plan', label: 'Lesson plan', hint: 'Objectives + warmup + practice' },
  { key: 'rubric', label: 'Rubric', hint: '4-level criteria for grading' },
];

export function ConvertLessonMenu({
  token,
  blocks,
  sourceModuleItemId,
}: {
  token: string;
  blocks: ContentBlockShape[];
  sourceModuleItemId: string;
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<Target | null>(null);

  const convertMutation = useMutation({
    mutationFn: (t: Target) =>
      api.ai.convert(token, {
        sourceText: extractText(blocks),
        targetType: t,
      }),
  });

  const sourceText = extractText(blocks);
  const tooLittle = sourceText.length < 30;

  return (
    <>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={tooLittle}
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border border-accent/30 bg-accent/5 px-3 py-1.5 text-xs font-semibold text-accent transition-colors hover:bg-accent/10 disabled:cursor-not-allowed disabled:opacity-50',
          )}
          title={
            tooLittle
              ? 'Add some text to the lesson first.'
              : 'Convert this lesson into a quiz, flashcards, slide deck, lesson plan, or rubric.'
          }
        >
          <Sparkles className="h-3.5 w-3.5" /> Convert lesson
          <ChevronDown className="h-3 w-3" />
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-xl border border-border bg-card shadow-lg"
            onClick={() => setOpen(false)}
          >
            {TARGETS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  setTarget(t.key);
                  void convertMutation.mutateAsync(t.key);
                }}
                className="block w-full px-3 py-2 text-left text-xs hover:bg-secondary"
              >
                <div className="font-semibold">{t.label}</div>
                <div className="text-muted-foreground">{t.hint}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {(target !== null || convertMutation.isPending) && (
        <PreviewModal
          target={target}
          isPending={convertMutation.isPending}
          isError={convertMutation.isError}
          result={convertMutation.data ?? null}
          token={token}
          sourceModuleItemId={sourceModuleItemId}
          onClose={() => {
            setTarget(null);
            convertMutation.reset();
          }}
        />
      )}
    </>
  );
}

function extractText(blocks: ContentBlockShape[]): string {
  const out: string[] = [];
  for (const b of blocks) {
    const p = parsePayload(b.type, b.payloadJson);
    if (p.type === 'text' && p.text.trim()) out.push(p.text.trim());
    else if (p.type === 'heading' && p.text.trim()) out.push(p.text.trim());
    else if (p.type === 'document' && p.html.trim()) {
      out.push(
        p.html
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim(),
      );
    }
  }
  return out.join('\n\n');
}

function PreviewModal({
  target,
  isPending,
  isError,
  result,
  token,
  sourceModuleItemId,
  onClose,
}: {
  target: Target | null;
  isPending: boolean;
  isError: boolean;
  result: { targetType: string; result: string; simulated?: boolean } | null;
  token: string;
  sourceModuleItemId: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { orgSlug = '', courseSlug = '' } = useParams();

  const onCopy = () => {
    if (!result?.result) return;
    void navigator.clipboard?.writeText(result.result).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  // Save-as-Assessment is only enabled for `quiz` target where the JSON
  // matches our expected shape. Other targets (slide_deck, lesson_plan, etc)
  // need their own materializers — they ship next.
  const parsedQuiz = parseQuizResult(result?.result);
  const canSaveQuiz = target === 'quiz' && parsedQuiz !== null && !result?.simulated;

  const saveQuizMutation = useMutation({
    mutationFn: () => {
      if (!parsedQuiz) throw new Error('quiz_parse_failed');
      return api.assessments.fromQuizConversion(token, {
        sourceModuleItemId,
        title: 'Generated Quiz',
        questions: parsedQuiz,
      });
    },
    onSuccess: ({ moduleItemId }) => {
      // Land in the assessment editor so the instructor can tweak before
      // releasing to students.
      navigate(`/c/${orgSlug}/${courseSlug}/assessments/${moduleItemId}/edit`);
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            <h2 className="font-display text-lg font-semibold">{labelFor(target)} preview</h2>
            {result?.simulated && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                Simulated · set AIML_API_KEY
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 text-sm">
          {isPending ? (
            <div className="flex flex-col items-center justify-center gap-3 p-8 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-accent" />
              EduBorg is converting…
            </div>
          ) : isError ? (
            <p className="text-destructive">
              Couldn't run the conversion. Try again, or pick a different target.
            </p>
          ) : result ? (
            <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-card p-3 text-xs">
              {pretty(result.result)}
            </pre>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card/50 px-5 py-3 text-xs text-muted-foreground">
          {target !== 'quiz' && (
            <span className="mr-auto">
              Save-as-item for {labelFor(target).toLowerCase()}s ships next — for now, copy the
              JSON.
            </span>
          )}
          {saveQuizMutation.isError && (
            <span className="mr-auto text-destructive">Couldn't save. Try again.</span>
          )}
          <button
            type="button"
            disabled={!result?.result}
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-secondary disabled:opacity-50"
          >
            <ClipboardCheck className="h-3 w-3" />
            {copied ? 'Copied!' : 'Copy JSON'}
          </button>
          {target === 'quiz' && (
            <button
              type="button"
              disabled={!canSaveQuiz || saveQuizMutation.isPending}
              onClick={() => void saveQuizMutation.mutateAsync()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              title={
                !canSaveQuiz
                  ? result?.simulated
                    ? 'Set AIML_API_KEY to enable saving as a real Assessment.'
                    : "The model output didn't match the expected quiz shape."
                  : 'Create an Assessment in this module + pre-fill the questions.'
              }
            >
              {saveQuizMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save as Assessment
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

function labelFor(t: Target | null): string {
  return TARGETS.find((x) => x.key === t)?.label ?? 'Conversion';
}

/**
 * The model returns valid JSON in real mode, and a placeholder string in
 * simulate mode. Try to pretty-print; fall back to raw if parse fails.
 */
function pretty(s: string): string {
  try {
    return JSON.stringify(
      JSON.parse(s.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '')),
      null,
      2,
    );
  } catch {
    return s;
  }
}

/**
 * Validate that the model's quiz output matches our expected shape:
 * `[{ prompt, options: string[2..8], correctIndex, explanation? }, ...]`.
 * Returns null if it doesn't — disables the Save button gracefully.
 */
function parseQuizResult(raw: string | undefined): Array<{
  prompt: string;
  options: string[];
  correctIndex: number;
  explanation?: string;
}> | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, ''));
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  const out: Array<{
    prompt: string;
    options: string[];
    correctIndex: number;
    explanation?: string;
  }> = [];
  for (const q of parsed) {
    if (!q || typeof q !== 'object') return null;
    const obj = q as Record<string, unknown>;
    const prompt = typeof obj.prompt === 'string' ? obj.prompt : null;
    const options = Array.isArray(obj.options)
      ? (obj.options.filter((o) => typeof o === 'string') as string[])
      : null;
    const correctIndex = typeof obj.correctIndex === 'number' ? obj.correctIndex : null;
    if (!prompt || !options || options.length < 2 || correctIndex === null) return null;
    if (correctIndex < 0 || correctIndex >= options.length) return null;
    const explanation = typeof obj.explanation === 'string' ? obj.explanation : undefined;
    out.push({
      prompt,
      options,
      correctIndex,
      ...(explanation ? { explanation } : {}),
    });
  }
  return out;
}
